import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { formatEuros, shortDate } from '../format';
import type { RoomOverview } from '../types';

type EditForm = {
  surfaceM2: string;
  hasBalcony: boolean;
  balconySurfaceM2: string;
  hasParking: boolean;
  active: boolean;
  defaultRentEuros: string;
  defaultChargesEuros: string;
};

function toEditForm(room: RoomOverview): EditForm {
  return {
    surfaceM2: room.surfaceM2 !== null ? String(room.surfaceM2) : '',
    hasBalcony: room.hasBalcony,
    balconySurfaceM2: room.balconySurfaceM2 !== null ? String(room.balconySurfaceM2) : '',
    hasParking: room.hasParking,
    active: room.active,
    defaultRentEuros: (room.defaultRentCents / 100).toString(),
    defaultChargesEuros: (room.defaultChargesCents / 100).toString(),
  };
}

export function RoomsPanel() {
  const [rooms, setRooms] = useState<RoomOverview[] | null>(null);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState<EditForm | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    try {
      const result = await api<{ rooms: RoomOverview[] }>('/api/admin/rooms');
      setRooms(result.rooms);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  function startEdit(room: RoomOverview): void {
    setEditingId(room.id);
    setForm(toEditForm(room));
    setError('');
  }

  async function save(roomId: string, event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/rooms/${roomId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          surfaceM2: form.surfaceM2 ? Number(form.surfaceM2) : null,
          hasBalcony: form.hasBalcony,
          balconySurfaceM2: form.hasBalcony && form.balconySurfaceM2 ? Number(form.balconySurfaceM2) : null,
          hasParking: form.hasParking,
          active: form.active,
          defaultRentCents: Math.round(Number(form.defaultRentEuros) * 100),
          defaultChargesCents: Math.round(Number(form.defaultChargesEuros) * 100),
        }),
      });
      setEditingId('');
      setForm(null);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel rooms-panel">
      <div className="section-heading"><div><p className="eyebrow">Configuration</p><h2>Chambres</h2></div><span className="count-badge">{rooms?.length ?? 0}</span></div>
      <p className="section-intro">Surface, balcon, parking et loyer par défaut de chaque chambre, utilisés comme base pour un nouveau bail.</p>
      {error && <div className="inline-message error" role="alert">{error}</div>}
      {!rooms && !error && <p className="muted">Chargement…</p>}
      <div className="record-list">
        {rooms?.map((room) => (
          <article className="record-card" key={room.id}>
            <div className="record-heading">
              <div><strong>{room.name}</strong><span className="record-code">{room.code}</span></div>
              <span className={`status ${room.currentLease ? 'signed' : 'draft'}`}>{room.currentLease ? 'Occupée' : 'Libre'}</span>
            </div>
            {room.currentLease && (
              <p className="record-line">{room.currentLease.tenantName} · loyer {formatEuros(room.currentLease.rentCents + room.currentLease.chargesCents)} CC{room.currentLease.contractualEnd ? ` · jusqu'au ${shortDate(room.currentLease.contractualEnd)}` : ''}</p>
            )}
            {!room.currentLease && room.nextLease && (
              <p className="record-line muted">Prochain bail : {room.nextLease.tenantName} à partir du {shortDate(room.nextLease.effectiveFrom)}</p>
            )}
            {editingId === room.id && form ? (
              <form className="form-grid" onSubmit={(event) => void save(room.id, event)}>
                <label>Surface (m²)<input type="number" step="0.01" min="0" value={form.surfaceM2} onChange={(event) => setForm({ ...form, surfaceM2: event.target.value })} /></label>
                <label className="check-field"><input type="checkbox" checked={form.hasBalcony} onChange={(event) => setForm({ ...form, hasBalcony: event.target.checked })} /><span>Balcon</span></label>
                {form.hasBalcony && <label>Surface du balcon (m²)<input type="number" step="0.01" min="0" value={form.balconySurfaceM2} onChange={(event) => setForm({ ...form, balconySurfaceM2: event.target.value })} /></label>}
                <label className="check-field"><input type="checkbox" checked={form.hasParking} onChange={(event) => setForm({ ...form, hasParking: event.target.checked })} /><span>Place de parking</span></label>
                <label>Loyer HC par défaut (€)<input type="number" step="0.01" min="0" value={form.defaultRentEuros} onChange={(event) => setForm({ ...form, defaultRentEuros: event.target.value })} /></label>
                <label>Charges par défaut (€)<input type="number" step="0.01" min="0" value={form.defaultChargesEuros} onChange={(event) => setForm({ ...form, defaultChargesEuros: event.target.value })} /></label>
                <label className="check-field"><input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} /><span>Chambre active</span></label>
                <div className="span-2 record-actions">
                  <button type="button" className="text-button" onClick={() => { setEditingId(''); setForm(null); }}>Annuler</button>
                  <button className="button primary compact" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
                </div>
              </form>
            ) : (
              <div className="record-line">
                <span>{room.surfaceM2 ?? '—'} m² · {room.hasBalcony ? `balcon ${room.balconySurfaceM2 ?? ''} m²` : 'sans balcon'} · {room.hasParking ? 'parking' : 'sans parking'}</span>
                <span>Défaut : {formatEuros(room.defaultRentCents)} HC + {formatEuros(room.defaultChargesCents)} charges</span>
                <button className="text-button" onClick={() => startEdit(room)}>Modifier</button>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
